import { useMemo, useState } from 'react';
import type { SheetKind } from '@elec/domain';
import { pickMaybe, pick, type Locale, type MessageKey, type Translate } from '@elec/i18n';
import type { Macro, UnplacedPoint } from '@elec/editor';
import type { LibraryItem } from '@elec/sync';
import {
  getSymbol,
  searchSymbols,
  SYMBOLS,
  symbolsForView,
  userSymbols,
  type SymbolCategory,
  type SymbolDef,
} from '@elec/symbols';
import { SymbolIcon } from './SymbolIcon.tsx';
import { Icon } from './ui.tsx';

const ORDER: SymbolCategory[] = [
  'distribution',
  'protection',
  'outlet',
  'lighting',
  'command',
  'appliance',
  'generation',
  'control',
  'motor',
  'signalling',
  'connection',
  'earth',
  'annotation',
  'user',
];

export function Palette(props: {
  sheetKind: SheetKind;
  locale: Locale;
  t: Translate;
  active: string | null;
  onPick(id: string): void;
  /** Position plans: single-line points not yet placed (same code on both documents). */
  unplaced?: UnplacedPoint[];
  activePreset?: string;
  onPlace?(u: UnplacedPoint): void;
  /** The project's user symbols (identity changes when they change). */
  userSymbolsKey: unknown;
  onNewSymbol(): void;
  onEditSymbol(id: string): void;
  macros: Macro[];
  onInsertMacro(m: Macro): void;
  onDeleteMacro(m: Macro): void;
  onLibrary(action: 'export-symbols' | 'import' | 'export-macros'): void;
  /** Symbols and macros shared by the cloud workspace. */
  teamItems?: LibraryItem[];
  onInsertTeamItem?(item: LibraryItem): void;
  onShareToTeam?(): void;
  /** Folded to a narrow rail of symbol icons (names in tooltips). */
  folded: boolean;
  onToggleFold(): void;
}) {
  const { t, locale, folded } = props;
  const [query, setQuery] = useState('');
  const [all, setAll] = useState(false);
  const groups = useMemo(() => {
    const everything = [...SYMBOLS, ...userSymbols()];
    const pool = all || props.sheetKind === 'board' ? everything : symbolsForView(props.sheetKind);
    // Folded, the search box is hidden: show every symbol of the sheet, not the last search.
    const q = folded ? '' : query;
    const found = searchSymbols(q, q ? everything : pool);
    const by = new Map<SymbolCategory, SymbolDef[]>();
    for (const s of found) by.set(s.category, [...(by.get(s.category) ?? []), s]);
    return ORDER.filter((c) => by.has(c)).map((c) => ({ cat: c, items: by.get(c)! }));
  }, [query, folded, all, props.sheetKind, props.userSymbolsKey]);

  return (
    <aside
      className={`palette${folded ? ' folded' : ''}`}
      aria-label={t('panel.symbols')}
      data-testid="palette"
    >
      <div className="palette-head">
        <div className="palette-title">
          {!folded && <h2>{t('panel.symbols')}</h2>}
          <button
            className="icon-btn palette-fold"
            onClick={props.onToggleFold}
            title={folded ? t('palette.unfold') : t('palette.fold')}
            aria-label={folded ? t('palette.unfold') : t('palette.fold')}
            aria-expanded={!folded}
            data-testid="palette-fold"
          >
            <Icon name={folded ? 'pal-unfold' : 'pal-fold'} />
          </button>
        </div>
        {!folded && (
          <>
            <label className="search">
              <Icon name="search" size={15} />
              <input
                type="search"
                value={query}
                placeholder={t('panel.search')}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && groups[0]) props.onPick(groups[0].items[0]!.id);
                  if (e.key === 'Escape') setQuery('');
                }}
                data-testid="symbol-search"
              />
            </label>
            <label className="check">
              <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
              {t('panel.allSheets')}
            </label>
          </>
        )}
      </div>
      <div className="palette-list">
        {props.unplaced && (
          <section className="to-place" data-testid="to-place">
            <h4>
              {t('plan.toPlace')} <span className="count">{props.unplaced.length}</span>
            </h4>
            {props.unplaced.length === 0 ? (
              <p className="muted small pad">{t('plan.allPlaced')}</p>
            ) : (
              <ul className="chips">
                {props.unplaced.map((u) => {
                  const def = getSymbol(u.symbolId);
                  return (
                    <li key={u.tag}>
                      <button
                        className={props.activePreset === u.tag ? 'active' : ''}
                        title={
                          folded
                            ? `${u.tag} — ${pickMaybe(def?.name, locale) ?? ''}`
                            : pickMaybe(def?.name, locale)
                        }
                        onClick={() => props.onPlace?.(u)}
                        data-place={u.tag}
                      >
                        {def && <SymbolIcon def={def} size={20} />}
                        <strong>{u.tag}</strong>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
        {groups.length === 0 && !folded && <p className="muted pad">{t('panel.noResults')}</p>}
        {groups.map((g) => (
          <section key={g.cat}>
            {folded ? (
              <hr className="palette-sep" title={t(`cat.${g.cat}` as MessageKey)} />
            ) : (
              <h4>{t(`cat.${g.cat}` as MessageKey)}</h4>
            )}
            <ul>
              {g.items.map((s) => (
                <li key={s.id}>
                  <button
                    className={props.active === s.id ? 'active' : ''}
                    onClick={() => props.onPick(s.id)}
                    title={pick(s.name, locale)}
                    aria-label={folded ? pick(s.name, locale) || s.name.fr || s.name.en : undefined}
                    data-symbol={s.id}
                  >
                    <SymbolIcon def={s} />
                    {!folded && <span>{pick(s.name, locale) || s.name.fr || s.name.en}</span>}
                  </button>
                  {!folded && s.id.startsWith('user.') && (
                    <button
                      className="mini"
                      title={t('sym.edit')}
                      aria-label={t('sym.edit')}
                      onClick={() => props.onEditSymbol(s.id)}
                    >
                      ✎
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
        {!folded && (
          <>
            <section data-testid="macros">
              <h4>{t('macro.title')}</h4>
              {props.macros.length === 0 ? (
                <p className="muted small pad">{t('macro.none')}</p>
              ) : (
                <ul>
                  {props.macros.map((m) => (
                    <li key={m.id}>
                      <button
                        onClick={() => props.onInsertMacro(m)}
                        title={t('macro.insert')}
                        data-macro={m.name}
                      >
                        <span className="macro-badge">{m.data.elements.length}</span>
                        <span>{m.name}</span>
                      </button>
                      <button
                        className="mini"
                        title={t('macro.delete')}
                        aria-label={t('macro.delete')}
                        onClick={() => props.onDeleteMacro(m)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            {!!props.teamItems?.length && (
              <section data-testid="team-library">
                <h4>{t('cloud.teamLibrary')}</h4>
                <ul>
                  {props.teamItems.map((i) => (
                    <li key={`${i.kind}:${i.id}`}>
                      <button onClick={() => props.onInsertTeamItem?.(i)} data-team-item={i.name}>
                        {i.kind === 'symbol' ? (
                          <SymbolIcon def={i.data as SymbolDef} />
                        ) : (
                          <span className="macro-badge">M</span>
                        )}
                        <span>{i.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <div className="palette-actions">
              <button onClick={props.onNewSymbol} data-testid="new-symbol">
                {t('sym.new')}
              </button>
              <button onClick={() => props.onLibrary('import')}>{t('lib.import')}</button>
              <button onClick={() => props.onLibrary('export-symbols')}>{t('lib.exportSymbols')}</button>
              <button onClick={() => props.onLibrary('export-macros')}>{t('lib.exportMacros')}</button>
              {props.onShareToTeam && (
                <button onClick={props.onShareToTeam} data-testid="share-to-team">
                  {t('cloud.shareToTeam')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
